/**
 * Équipe du compte — **le client compose la sienne**.
 *
 * Sur la plateforme de test, il ouvre son compte lui-même puis invite qui il
 * veut. En production, INFRATEX ouvre le compte et son premier propriétaire, et
 * la suite lui appartient de la même façon : ce n'est pas à l'exploitant de
 * savoir qui travaille chez son client.
 *
 * L'écran n'apparaît qu'aux rôles qui portent le droit `users:manage` —
 * propriétaire et administrateur. Le serveur le revérifie : masquer un onglet
 * n'a jamais protégé une route.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  api,
  type ContexteCompte,
  type DescriptionRole,
  type RoleClient,
  type UtilisateurAdmin,
} from '../../api/client';
import {
  Carte,
  Champ,
  Chargement,
  EtatVide,
  Grille,
  MessageErreur,
  MessageSucces,
  PuceLibre,
} from '../../components/Ui';
import { formaterDate } from '../../lib/format';
import { LIBELLE_ROLE, ROLES, USAGE_ROLE, CatalogueDesRoles } from '../admin/Utilisateurs';

export function Equipe() {
  const [equipe, setEquipe] = useState<UtilisateurAdmin[] | null>(null);
  const [contexte, setContexte] = useState<ContexteCompte | null>(null);
  const [roles, setRoles] = useState<DescriptionRole[]>([]);
  const [erreur, setErreur] = useState<unknown>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [ajout, setAjout] = useState(false);

  const charger = useCallback(() => {
    api.equipe
      .lister()
      .then(({ data, context, roles: catalogue }) => {
        setEquipe(data);
        setContexte(context);
        setRoles(catalogue);
      })
      .catch(setErreur);
  }, []);

  useEffect(charger, [charger]);

  async function modifier(id: string, champs: Parameters<typeof api.equipe.modifier>[1]) {
    setErreur(null);
    setSucces(null);
    try {
      const { user, revoked_sessions } = await api.equipe.modifier(id, champs);
      setSucces(
        revoked_sessions > 0
          ? `${user.email} : droits mis à jour, ${revoked_sessions} session(s) fermée(s). La personne devra se reconnecter.`
          : `${user.email} : droits mis à jour.`,
      );
      charger();
    } catch (e) {
      setErreur(e);
    }
  }

  if (erreur && !equipe) return <MessageErreur erreur={erreur} />;
  if (!equipe) return <Chargement quoi="Chargement de l’équipe…" />;

  const unSeulProprietaire = (contexte?.proprietaires_actifs ?? 0) <= 1;

  return (
    <>
      <Carte
        titre="Votre équipe"
        action={
          <button className="secondaire" onClick={() => setAjout((v) => !v)}>
            {ajout ? 'Fermer' : 'Inviter quelqu’un'}
          </button>
        }
      >
        {erreur ? <MessageErreur erreur={erreur} /> : null}
        {succes && <MessageSucces>{succes}</MessageSucces>}

        {ajout && (
          <FormulaireInvitation
            roles={roles}
            onCree={(message) => {
              setAjout(false);
              setErreur(null);
              setSucces(message);
              charger();
            }}
          />
        )}

        {unSeulProprietaire && (
          <div className="aide">
            Vous êtes le seul propriétaire actif de ce compte. Votre rôle ne peut pas être retiré
            tant que quelqu’un d’autre n’a pas pris le relais — sinon plus personne ne pourrait
            gérer les {contexte?.cles_actives ?? 0} clé(s) API active(s) ni l’équipe.
          </div>
        )}

        {equipe.length === 0 ? (
          <EtatVide titre="Vous êtes seul sur ce compte">
            Invitez vos collègues et donnez à chacun le rôle qui correspond à son travail.
          </EtatVide>
        ) : (
          <div className="tableau-conteneur" style={{ marginTop: 12 }}>
            <table style={{ minWidth: 780 }}>
              <thead>
                <tr>
                  <th>Personne</th>
                  <th>Rôle</th>
                  <th>Droits</th>
                  <th>Dernière connexion</th>
                  <th>État</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {equipe.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{u.full_name ?? u.email}</div>
                      <div style={{ color: 'var(--muet)', fontSize: 12 }}>{u.email}</div>
                    </td>
                    <td>
                      <select
                        value={u.role}
                        onChange={(e) => modifier(u.id, { role: e.target.value as RoleClient })}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {LIBELLE_ROLE[r]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={{ maxWidth: 260 }}>
                      <div style={{ color: 'var(--muet)', fontSize: 12 }}>
                        {u.permissions.length} droit(s) — {USAGE_ROLE[u.role]}
                      </div>
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {u.last_login_at ? formaterDate(u.last_login_at) : 'Jamais connecté'}
                    </td>
                    <td>
                      {u.is_active ? (
                        <PuceLibre ton="succes">Actif</PuceLibre>
                      ) : (
                        <PuceLibre ton="echec">Désactivé</PuceLibre>
                      )}
                    </td>
                    <td>
                      <button
                        className="secondaire"
                        onClick={() => modifier(u.id, { is_active: !u.is_active })}
                      >
                        {u.is_active ? 'Désactiver' : 'Réactiver'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="aide">
          Un changement de rôle <strong>ferme les sessions ouvertes</strong> de la personne : elle
          se reconnecte et repart avec ses droits du moment. Vous ne pouvez ni vous désactiver, ni
          changer votre propre rôle — il faut être deux.
        </div>
      </Carte>

      <CatalogueDesRoles roles={roles} />
    </>
  );
}

function FormulaireInvitation({
  roles,
  onCree,
}: {
  roles: DescriptionRole[];
  onCree: (message: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [nom, setNom] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [role, setRole] = useState<RoleClient>('member');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);

  const description = roles.find((r) => r.role === role);

  async function soumettre() {
    setEnvoi(true);
    setErreur(null);
    try {
      const { user } = await api.equipe.creer({
        email,
        password: motDePasse,
        full_name: nom || undefined,
        role,
      });
      onCree(
        `${user.email} rejoint votre équipe avec le rôle « ${LIBELLE_ROLE[user.role]} ». ` +
          'Transmettez-lui son mot de passe par un canal distinct de la console, et demandez-lui de le changer.',
      );
    } catch (e) {
      setErreur(e);
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
      {erreur ? <MessageErreur erreur={erreur} /> : null}

      <Grille>
        <Champ libelle="Nom" valeur={nom} onChange={setNom} />
        <Champ libelle="Adresse e-mail" type="email" valeur={email} onChange={setEmail} requis />
        <Champ
          libelle="Mot de passe provisoire"
          type="password"
          valeur={motDePasse}
          onChange={setMotDePasse}
          aide="10 caractères minimum."
          requis
        />
        <div className="champ">
          <label htmlFor="role-invite">Rôle</label>
          <select
            id="role-invite"
            value={role}
            onChange={(e) => setRole(e.target.value as RoleClient)}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {LIBELLE_ROLE[r]}
              </option>
            ))}
          </select>
          <div className="aide" style={{ marginTop: 4 }}>
            {USAGE_ROLE[role]}
          </div>
        </div>
      </Grille>

      {description && (
        <div className="aide" style={{ marginTop: 0 }}>
          <strong>Ce rôle donne :</strong> {description.permissions.map((p) => p.label).join(' · ')}
        </div>
      )}

      <div>
        <button onClick={soumettre} disabled={envoi || !email || motDePasse.length < 10}>
          {envoi ? 'Création…' : 'Créer l’accès'}
        </button>
      </div>
    </div>
  );
}
